"use client"

export const dynamic = 'force-dynamic'

import React, { useState, useEffect, useMemo, useRef } from "react"
import Image from "next/image"
import dynamicImport from "next/dynamic"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Check, MapPin, Mail, Phone, Clock, Star } from "lucide-react"
import { useI18n } from "@/context/i18n"

// Componente de carga para el mapa
const MapLoader = () => {
  const { t } = useI18n()
  return (
    <div className="w-full h-[500px] flex items-center justify-center bg-gray-100 rounded-lg">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#fb0009] mx-auto mb-4"></div>
        <p className="text-gray-600">{t('loading_map')}</p>
      </div>
    </div>
  )
}

// Componente de carga genérico
const ComponentLoader = () => (
  <div className="w-full h-32 flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#fb0009]"></div>
  </div>
)

// Lazy load de componentes pesados que no son críticos para FCP
const InteractiveMap = dynamicImport(
  () => import("@/components/interactive-map"),
  { ssr: false, loading: () => <MapLoader /> }
)

const Modal = dynamicImport(
  () => import("@/components/Modal"),
  { ssr: false }
)

const HomeTour = dynamicImport(
  () => import("@/components/home-tour"),
  { ssr: false, loading: () => <ComponentLoader /> }
)

const Relevantes = dynamicImport(
  () => import("@/components/Relevantes"),
  { ssr: false, loading: () => <ComponentLoader /> }
)

const JoinChat = dynamicImport(
  () => import("@/components/JoinChat"),
  { ssr: false, loading: () => <ComponentLoader /> }
)

// Lazy load CTASection to reduce initial bundle
const CTASection = dynamicImport(
  () => import("@/app/components/cta-section"),
  { ssr: false, loading: () => <ComponentLoader /> }
)

// Lazy load ReviewsSection to reduce initial bundle
const ReviewsSection = dynamicImport(
  () => import("@/app/components/reviews-section"),
  { ssr: false, loading: () => <ComponentLoader /> }
)

// Lazy load PromoBanner to reduce initial bundle
const PromoBanner = dynamicImport(
  () => import("@/components/PromoBanner"),
  { ssr: false, loading: () => <ComponentLoader /> }
)

// Componentes críticos - importación normal
import type { Office } from "@/components/interactive-map"
import { offices } from "@/components/interactive-map"
import Header from "@/components/Header"
import HeroSearch from "@/components/HeroSearch"
import ServicesStepper from "@/components/ServicesStepper"
import SectionTitle from "@/components/SectionTitle"
import SecondaryServicesNav from "@/components/SecondaryServicesNav"
import SafeImage from '@/components/SafeImage'
import TextCarousel from "@/components/TextCarousel"

export default function SeccionPage() {
  const { t, lang } = useI18n()
  const [selected, setSelected] = useState<Office>(offices[0])
  type ServiceKey = 'lets-study' | 'lets-travel' | 'lets-participaciones'
  const [selectedService, setSelectedService] = useState<ServiceKey>('lets-study')

  type TrendingItem = {
    title?: string | null
    title_en?: string | null
    description?: string | null
    description_en?: string | null
    image?: string | null
    href?: string | null
    badge?: string | null
    badge_en?: string | null
    category?: string | null
    category_en?: string | null
    type?: string | null
  }

  const acreditaciones = [
    "/acreditaciones/LET_PARTNER_AMTE.jpg",
    "/acreditaciones/LET_ACREDITACION_ICEF.jpg",
    "/acreditaciones/LET_ACREDITACION_IALC.jpg",
    "/acreditaciones/CASTA-LATINA.png",
    "/acreditaciones/ST-REFERENCE-CHECKED.jpg",
  ]

  const afiliaciones = [
    "/acreditaciones/LET_AFILIACION_CANCHAN-MEXICO.jpg",
    "/acreditaciones/LET_AFILIACION_GMA.jpg",
    "/acreditaciones/LET_PARTNER_CAPSI-ACEPI.jpg",
    "/acreditaciones/LET_PARTNER_CSP.jpg",
    "/acreditaciones/LET_PARTNER_EDUCANADA.jpg",
    "/acreditaciones/LET_PARTNER_SAFE-TRAVELS.jpg",
    "/acreditaciones/FELCA-1.png",
    "/acreditaciones/LANGUAGE-CANADA.png",
    "/acreditaciones/STUDY-TRAVEL.png",
    "/acreditaciones/verifica-y-viaja.png",
    "/acreditaciones/rnt.png",
  ]

  const HERO_VIDEO_SRC = "https://res.cloudinary.com/dq1cuhk5a/video/upload/v1776126685/20_a%C3%B1os_let_ow3qba.mp4"
  // Use Cloudinary auto-format/quality for optimized poster instead of Google Drive
  const HERO_VIDEO_POSTER = "https://res.cloudinary.com/dlkbjyrtc/image/upload/v1768589307/Untitled_Project_4_rt65zz.jpg"
  const TRENDING_CACHE_KEY = "let-trending-cache-v1"
  const TRENDING_CACHE_TTL = 1000 * 60 * 60 * 3 // 3 hours

  const [trending, setTrending] = useState<TrendingItem[]>([])
  const [trendingLoaded, setTrendingLoaded] = useState(false)
  const [shouldLoadTrendingData, setShouldLoadTrendingData] = useState(false)
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false)
  const [isVideoReady, setIsVideoReady] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const videoContainerRef = useRef<HTMLDivElement | null>(null)
  const trendingSentinelRef = useRef<HTMLDivElement | null>(null)

  const relevantFromTrending = useMemo(() => {
    return (trending || []).map((it) => ({
      title: (lang === 'EN' ? (it.title_en ?? it.title) : (it.title ?? it.title_en)) || '',
      title_en: it.title_en ?? undefined,
      description: (lang === 'EN' ? (it.description_en ?? it.description) : (it.description ?? it.description_en)) ?? undefined,
      description_en: it.description_en ?? undefined,
      image: it.image ?? undefined,
      href: it.href ?? undefined,
      badge: (lang === 'EN' ? (it.badge_en ?? it.badge) : (it.badge ?? it.badge_en)) ?? undefined,
      badge_en: it.badge_en ?? undefined,
    }))
  }, [trending, lang])

  useEffect(() => {
    let cancelled = false
    fetch('/api/trending', { cache: 'no-store' })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(String(r.status))))
      .then((json) => {
        if (cancelled) return
        const items = Array.isArray((json as any)?.items) ? (json as any).items : []
        setTrending(items as TrendingItem[])
        setTrendingLoaded(true)
      })
      .catch(() => { if (!cancelled) setTrendingLoaded(true) })
    return () => { cancelled = true }
  }, [])

  // Respect reduced motion and load hero video after idle to protect LCP
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setPrefersReducedMotion(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    let cancelled = false
    let idleId: any
    const enable = () => { if (!cancelled) setShouldLoadVideo(true) }
    // Prefer requestIdleCallback; fallback to a short timeout
    const ric: any = (window as any).requestIdleCallback
    if (typeof ric === 'function') {
      idleId = ric(enable, { timeout: 2000 })
      return () => {
        cancelled = true
        const cic: any = (window as any).cancelIdleCallback
        if (typeof cic === 'function') cic(idleId)
      }
    } else {
      const t = window.setTimeout(enable, 1200)
      return () => { cancelled = true; window.clearTimeout(t) }
    }
  }, [])

  // Partners logos are defined above (populated arrays are used)
  const introStats = [
    {
      value: t('home_intro_stat_years_value'),
      label: t('home_intro_stat_years_label'),
    },
    {
      value: t('home_intro_stat_students_value'),
      label: t('home_intro_stat_students_label'),
    },
    {
      value: t('home_intro_stat_countries_value'),
      label: t('home_intro_stat_countries_label'),
    },

  ].filter(
    (stat) =>
      Boolean(stat.value) &&
      Boolean(stat.label) &&
      !stat.value.startsWith('home_intro_stat_') &&
      !stat.label.startsWith('home_intro_stat_')
  )

  return (
    <div className="min-h-screen bg-white relative overflow-x-hidden" style={{ fontFamily: 'Barlow, sans-serif' }}>
      <PromoBanner />
      <Header />
      <HomeTour />

      <main className="pt-0 justify-paragraphs">
        {/* Hero de dos columnas */}
        <section id="intro-hero" className="bg-white py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
              <div className="order-1 lg:order-1 lg:col-start-2 self-starts">
                <div className="pt-8">
                  <h1 className="text-2xl md:text-3xl lg:text-5xl font-black text-dark leading-tight text-center lg:text-left">
                    {t('home_intro_title')}
                  </h1>
                </div>
              </div>



              <div className="order-3 lg:order-2 lg:col-start-2">
                <div className="mb-6">
                  <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                    {t('home_intro_paragraph')}
                    <br />
                    <br />
                    {t('home_intro_end')}
                  </p>
                </div>
                <br />

                {/* EstadÃ­sticas */}
                {introStats.length > 0 && (
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {introStats.map((stat, idx) => (
                      <div key={`stat-${idx}`} className="text-center bg-white rounded-lg">
                        <div className="text-3xl md:text-4xl font-black text-[#FB0009] mb-1">{stat.value}</div>
                        <div className="text-xs md:text-sm text-gray-600 font-semibold">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="order-2 lg:order-1 lg:col-start-1 self-start lg:-mt-20">
                <div
                  className="relative rounded-2xl overflow-hidden shadow-none lg:shadow-2xl aspect-[16/11] bg-black"
                  ref={videoContainerRef}
                >
                  <Image
                    src={HERO_VIDEO_POSTER}
                    alt={t('home_intro_video_alt') || 'Experiencias LET en acción'}
                    fill
                    priority
                    fetchPriority="high"
                    sizes="(min-width: 1280px) 40vw, (min-width: 1024px) 45vw, 100vw"
                    className={`object-cover transition-opacity duration-500 ease-out ${shouldLoadVideo && isVideoReady ? 'opacity-0' : 'opacity-100'}`}
                  />
                  {shouldLoadVideo && !prefersReducedMotion && (
                    <video
                      key="hero-video"
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out ${isVideoReady ? 'opacity-100' : 'opacity-0'}`}
                      src={HERO_VIDEO_SRC}
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      poster={HERO_VIDEO_POSTER}
                      onLoadedData={() => setIsVideoReady(true)}
                    />
                  )}
                  {!shouldLoadVideo && (
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-black/20 to-transparent" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>



        {/* Hero dinÃ¡mico con tabs y marquee, sin buscador */}
        <div className="text-center mb-2 md:mb-2">
          <br />

          <SectionTitle text={t('home_what_we_offer_title')} variant="light" className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black" />
        </div>
        <HeroSearch

          variant="home"
          selectedKey={selectedService as any}
          onSelect={(key) => {
            // Only accept the known home keys to keep local state type-safe
            const allowed: ServiceKey[] = ['lets-study', 'lets-travel', 'lets-participaciones']
            if ((allowed as string[]).includes(key)) {
              setSelectedService(key as ServiceKey)
            }
          }}
        />
        {/* Secondary services navigation after hero */}
        <SecondaryServicesNav />

        <section id="home-services" className="bg-white relative overflow-hidden">
          <div className="container mx-auto px-4 md:px-8">

            <div ref={trendingSentinelRef} aria-hidden="true" className="h-px w-full" />

            <Relevantes
              title={t('home_relevantes_title')}
              subtitle={t('home_relevantes_subtitle')}
              items={relevantFromTrending}
              className="pt-0"
            />
            <br />


            <div className="text-center mb-8 md:mb-10">
              <SectionTitle text={t('services_title')} variant="light" className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black mb-6 md:mb-8" />
            </div>
            <div className="max-w-6xl mx-auto">
              <ServicesStepper service={selectedService} />
            </div>
          </div>
        </section>
        <br />
        <br />

        {/* Promo banner moved below the primary hero/services to avoid above-the-fold CLS */}

        <section id="alianzas" className="py-12 md:py-16 bg-white relative scroll-mt-32 md:scroll-mt-40">
          <div className="container mx-auto px-4 md:px-8">
            <div className="grid lg:grid-cols-2 gap-10 md:gap-12 items-center">
              {/* Left text block (unified) */}
              <div className="max-w-xl">
                <div className="text-sm font-bold tracking-widest uppercase text-neutral-500 mb-3">{t('home_partners_label')}</div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight text-black mb-4 uppercase" style={{ fontFamily: 'DIN, sans-serif' }}>
                  {t('home_partners_title1')}
                  <br />
                  {t('home_partners_title2')}
                </h2>
                <div className="h-1.5 w-24 bg-[#fb0009] rounded-full mb-6"></div>
                <p className="text-black/80 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {t('home_partners_paragraph')}
                </p>
              </div>

              {/* Right logos grid (combined) */}
              {/* GRID DE PARTNERS LAZY: se monta sólo al entrar en viewport para reducir Speed Index */}
              <LazyPartnersGrid images={[...acreditaciones, ...afiliaciones]} />
            </div>
          </div>
        </section>




        <section className="py-12 md:py-16 bg-black text-white relative overflow-hidden" id="office-locator">
          <div className="container mx-auto px-4 md:px-8 relative z-10">
            <div className="text-center mb-16 md:mb-20">
              <SectionTitle text={t('home_offices_title')} variant="dark" className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black mb-6 md:mb-8" />
              <p className="text-base md:text-lg lg:text-xl xl:text-2xl text-white max-w-4xl mx-auto leading-relaxed px-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('home_offices_subtitle')}
              </p>
            </div>

            {/* Horizontal selector */}
            <div className="max-w-7xl mx-auto mb-6 md:mb-8">
              <div className="flex flex-wrap justify-center gap-2 md:gap-3">
                {offices.map((o) => {
                  const active = selected.id === o.id
                  return (
                    <button
                      key={o.id}
                      onClick={() => setSelected(o)}
                      className={`px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base border rounded-full transition-colors ${active ? 'bg-[#fb0009] text-white border-[#fb0009]' : 'bg-white text-black border-gray-200 hover:border-[#fb0009] hover:text-[#fb0009]'}`}
                      aria-current={active ? 'true' : undefined}
                    >
                      {lang === 'EN' ? (o.location_en || o.location) : o.location}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 md:gap-12 max-w-7xl mx-auto" >
              <div className="lg:col-span-2 order-2 lg:order-1">
                <Card
                  className="overflow-hidden rounded-2xl md:rounded-3xl bg-white shadow-2xl"
                  style={{ minHeight: "300px", height: "100%" }}
                >
                  <InteractiveMap
                    selectedOffice={selected}
                    onSelect={setSelected}
                  />
                </Card>
              </div>

              <div className="space-y-6 md:space-y-8 order-1 lg:order-2">
                <Card className="p-6 md:p-8 bg-white rounded-2xl md:rounded-3xl shadow-xl text-black" id="office-details">
                  <div className="flex items-center space-x-4 mb-6 md:mb-8">
                    <div>
                      <h3 className="text-2xl md:text-3xl font-black text-black" style={{ fontFamily: 'DIN, sans-serif' }}>
                        {lang === 'EN' ? (selected.name_en || selected.name) : selected.name}
                      </h3>
                      <p className="text-black text-base md:text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {lang === 'EN' ? (selected.location_en || selected.location) : selected.location}, {lang === 'EN' ? (selected.country_en || selected.country) : selected.country}
                      </p>
                    </div>
                  </div>

                  <p className="text-black text-sm md:text-base mb-6 md:mb-8 leading-relaxed">
                    {lang === 'EN' ? (selected.description_en || selected.description) : selected.description}
                  </p>

                  <div className="space-y-3 md:space-y-4 text-black text-sm md:text-base">
                    <div className="flex items-start space-x-3 md:space-x-4">
                      <MapPin className="h-4 w-4 md:h-5 md:w-5 text-[#fb0009] mt-0.5 flex-shrink-0" />
                      <span className="leading-tight">{lang === 'EN' ? (selected.address_en || selected.address) : selected.address}</span>
                    </div>
                    <div className="flex items-center space-x-3 md:space-x-4">
                      <Phone className="h-4 w-4 md:h-5 md:w-5 text-[#fb0009] flex-shrink-0" />
                      <a href={`tel:${(selected.phone || '').replace(/[^+\d]/g, '')}`} className="hover:text-[#fb0009] transition-colors duration-300">{selected.phone}</a>
                    </div>
                    <div className="flex items-center space-x-3 md:space-x-4">
                      <Mail className="h-4 w-4 md:h-5 md:w-5 text-[#fb0009] flex-shrink-0" />
                      <a href={`mailto:${selected.email || ''}`} className="hover:text-[#fb0009] transition-colors duration-300 break-all">{selected.email}</a>
                    </div>
                    <div className="flex items-center space-x-3 md:space-x-4">
                      <Clock className="h-4 w-4 md:h-5 md:w-5 text-[#fb0009] flex-shrink-0" />
                      <span>{lang === 'EN' ? (selected.hours_en || selected.hours) : selected.hours}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mt-6 md:mt-8">
                    <Button asChild className="bg-[#25D366] text-white text-sm md:text-base py-3 md:py-4 font-bold rounded-lg md:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 focus:ring-2 focus:ring-[#1DA851] focus:ring-offset-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      <a href={`https://wa.me/${(selected.phone || '').replace(/[^\d]/g, '')}?text=${encodeURIComponent(
                        lang === 'EN'
                          ? `Hello! I'm contacting your ${selected.name} office from your website and I'd like more information.`
                          : `¡Hola! Me contacto a su oficina de ${selected.name} desde su sitio web y me gustaría más información.`
                      )}`} target="_blank" rel="noopener noreferrer" aria-label={`${t('whatsapp_title') || 'WhatsApp'} ${selected.name}`}>
                        <svg fill="#ffffff" version="1.1" id="Capa_1" width="800px" height="800px" viewBox="0 0 30.667 30.667" stroke="#ffffff">

                          <g id="SVGRepo_bgCarrier" strokeWidth="0" />

                          <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" />

                          <g id="SVGRepo_iconCarrier"> <g> <path d="M30.667,14.939c0,8.25-6.74,14.938-15.056,14.938c-2.639,0-5.118-0.675-7.276-1.857L0,30.667l2.717-8.017 c-1.37-2.25-2.159-4.892-2.159-7.712C0.559,6.688,7.297,0,15.613,0C23.928,0.002,30.667,6.689,30.667,14.939z M15.61,2.382 c-6.979,0-12.656,5.634-12.656,12.56c0,2.748,0.896,5.292,2.411,7.362l-1.58,4.663l4.862-1.545c2,1.312,4.393,2.076,6.963,2.076 c6.979,0,12.658-5.633,12.658-12.559C28.27,8.016,22.59,2.382,15.61,2.382z M23.214,18.38c-0.094-0.151-0.34-0.243-0.708-0.427 c-0.367-0.184-2.184-1.069-2.521-1.189c-0.34-0.123-0.586-0.185-0.832,0.182c-0.243,0.367-0.951,1.191-1.168,1.437 c-0.215,0.245-0.43,0.276-0.799,0.095c-0.369-0.186-1.559-0.57-2.969-1.817c-1.097-0.972-1.838-2.169-2.052-2.536 c-0.217-0.366-0.022-0.564,0.161-0.746c0.165-0.165,0.369-0.428,0.554-0.643c0.185-0.213,0.246-0.364,0.369-0.609 c0.121-0.245,0.06-0.458-0.031-0.643c-0.092-0.184-0.829-1.984-1.138-2.717c-0.307-0.732-0.614-0.611-0.83-0.611 c-0.215,0-0.461-0.03-0.707-0.03S9.897,8.215,9.56,8.582s-1.291,1.252-1.291,3.054c0,1.804,1.321,3.543,1.506,3.787 c0.186,0.243,2.554,4.062,6.305,5.528c3.753,1.465,3.753,0.976,4.429,0.914c0.678-0.062,2.184-0.885,2.49-1.739 C23.307,19.268,23.307,18.533,23.214,18.38z" /> </g> </g>

                        </svg>
                        {t('whatsapp_button') || (lang === 'EN' ? 'WhatsApp' : 'WhatsApp')}
                      </a>
                    </Button>
                    <Button asChild variant="outline" className="bg-white text-[#fb0009] hover:bg-[#fb0009] text-sm md:text-base hover:text-white py-3 md:py-4 font-bold rounded-lg md:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      <a href={`mailto:${selected.email || ''}`} aria-label={`${t('email_cta')} ${selected.name}`}>
                        <Mail className="h-4 w-4 md:h-5 md:w-5 mr-2" /> {t('email_cta')}
                      </a>
                    </Button>
                  </div>
                </Card>
                {/* Removed vertical offices list in favor of horizontal selector above the map */}
              </div>
            </div>
          </div>
        </section>

        <ReviewsSection selected={selected} />

        <CTASection />

        {/* Removed floating language switcher; navbar toggle handles language */}
      </main>

      <JoinChat position="right" buttonDelay={3} />
    </div>
  )
}

// Componente lazy para logos (reduce 30+ requests en primer paint)
const LazyPartnersGrid: React.FC<{ images: string[] }> = ({ images }) => {
  const [visible, setVisible] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          setVisible(true)
          obs.disconnect()
        }
      })
    }, { rootMargin: '200px' })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  return (
    <div ref={ref} className="bg-white border border-gray-200 rounded-xl overflow-hidden min-h-[220px] flex items-center justify-center">
      {!visible && (
        <div className="w-full text-center py-12 animate-pulse text-sm text-gray-500">Cargando logos…</div>
      )}
      {visible && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 w-full">
          {images.map((src, idx) => (
            <div key={idx} className="flex items-center justify-center p-6 bg-white w-full h-full border-r border-b border-gray-200">
              <div className="relative w-full h-16 sm:h-20 lg:h-24">
                {/* Evitar problemas con Next/Image para dominios remotos: usar img directo */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={src} alt="Partner logo" className="object-contain w-full h-full" loading="lazy" referrerPolicy="no-referrer" />
              </div>
            </div>
          ))}
          {/* Rellenos para mantener los bordes de la cuadrícula completos */}
          {images.length % 4 !== 0 && Array.from({ length: 4 - (images.length % 4) }).map((_, i) => (
            <div key={`empty-lg-${i}`} className="hidden lg:flex bg-white w-full h-full border-r border-b border-gray-200" style={{ minHeight: '120px' }} />
          ))}
          {images.length % 3 !== 0 && Array.from({ length: 3 - (images.length % 3) }).map((_, i) => (
            <div key={`empty-sm-${i}`} className="hidden sm:flex lg:hidden bg-white w-full h-full border-r border-b border-gray-200" style={{ minHeight: '120px' }} />
          ))}
          {images.length % 2 !== 0 && Array.from({ length: 2 - (images.length % 2) }).map((_, i) => (
            <div key={`empty-base-${i}`} className="flex sm:hidden bg-white w-full h-full border-r border-b border-gray-200" style={{ minHeight: '120px' }} />
          ))}
        </div>
      )}
    </div>
  )
}













